import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { DateHelper } from 'apps/idl/src/helpers';
import { ETimetablePattern } from 'apps/idl/src/model';
import { IPatient } from 'apps/idl/src/modules/patients/model/IPatient';
import { takeUntil, tap } from 'rxjs/operators';
import { PatientsService } from '../../../../../modules/patients/services/patients.service';
import { LoaderService } from '../../../shared/services/loader.service';
import { PanneauService } from '../../../shared/services/panneau.service';
import { DestroyableComponentBase } from 'apps/idl/src/modules/utils/components/destroyable-component-base';

@Component({
	selector: 'di-recherche-patients',
	templateUrl: './recherche-patients.component.html',
	styleUrls: ['./recherche-patients.component.scss'],
})
export class RecherchePatientsComponent extends DestroyableComponentBase implements OnInit, OnDestroy {

	@Input()
	public displayCount = false;

	@Input()
	public mobileViewForce? = false;

	@Input()
	public patientOrdonnance = false;

	@Output()
	public onClickLigne = new EventEmitter<string>();

	public patients: IPatient[];
	public filteredPatients: IPatient[];

	public searchedValue: string = "";

	public patientsCount: number;
	public patientsCountMessage: string = "";

	public noResultMessage: string;

	constructor(
		public router: Router,
		private svcPatients: PatientsService,
		private svcPanneau: PanneauService,
		private svcLoader: LoaderService
	) { 
		super();
	}

	ngOnDestroy(): void {
		this.svcLoader.hideLoader();
	}

	ngOnInit() {
		this.svcLoader.showLoader("Récupération des patients...")
		this.svcPatients.patientChanges$.pipe(
			takeUntil(this.destroyed$)
		).subscribe(() => {
			this.initPatients();
		}); 
		this.initPatients();
	}

	public initPatients(): void {
		this.svcPatients.getSitePatients().pipe(
			tap((patients: IPatient[]) => {
				this.patients = this.mobileViewForce ? patients.filter(patient => !patient.deceased && !patient.isArchive) : patients.filter(patient => !patient.isArchive);
				this.patients = this.sortPatients(this.patients);
				this.filteredPatients = this.patients;
				this.getPatientsCount();
				this.svcLoader.hideLoader();
			})
		).subscribe();
	}

	sortPatients(patients: IPatient[]): IPatient[] {
		return patients.sort((a, b) => {
			// On utilise lastName si usualLastName est absent
			const usualLastNameA = a.usualLastName || a.lastName || '';
			const usualLastNameB = b.usualLastName || b.lastName || '';
			const compareUsualLastName = usualLastNameA.localeCompare(usualLastNameB, 'fr', { sensitivity: 'base' });
			if (compareUsualLastName !== 0) {
				return compareUsualLastName;
			}

			const lastNameA = a.lastName || '';
			const lastNameB = b.lastName || '';
			const compareLastName = lastNameA.localeCompare(lastNameB, 'fr', { sensitivity: 'base' });
			if (compareLastName !== 0) {
				return compareLastName;
			}

			const firstNameA = a.firstName || '';
			const firstNameB = b.firstName || '';
			return firstNameA.localeCompare(firstNameB, 'fr', { sensitivity: 'base' });
		});
	}

	onClickPatient(patientId: string) {
		if (this.onClickLigne.observers.length > 0) {
			this.onClickLigne.emit(patientId);
		} else {
			this.router.navigate(['/patients', patientId]);
		}
	}

	public filterPatients(newValue: string): void {
		this.searchedValue = newValue.trim().toLocaleLowerCase();

		if (this.searchedValue.length > 2) {
			const searchTerms = this.searchedValue.split(/\s+/).filter(term => term);
			this.filteredPatients = this.patients.filter(patient => {

				const firstName = patient.firstName ? patient.firstName.toLowerCase() : '';
				const lastName = patient.lastName ? patient.lastName.toLowerCase() : '';
				const usualLastName = patient.usualLastName ? patient.usualLastName.toLowerCase() : '';
				const formattedBirthDateSlash = DateHelper.transform(patient.birthDate, ETimetablePattern.dd_MM_yyyy_slash);

				const fullName = `${firstName} ${usualLastName} ${lastName} ${formattedBirthDateSlash}`.trim();

				return searchTerms.every(term => fullName.includes(term));
			});
		} else {
			this.filteredPatients = this.patients;
		}
		this.getPatientsCount();
	}

	public navigateToCreatePatient(): void {
		if (this.patientOrdonnance) {
			this.svcPanneau.close();
			this.router.navigate(['patients', 'add'], { state: { patientOrdonnance: true } })
		} else {
			this.router.navigate(['patients', 'add']);
		}

	}

	public getPatientsCount(): void {
		this.patientsCount = this.filteredPatients.length;
		this.patientsCountMessage = `${this.patientsCount > 0 ? this.patientsCount : "Aucun"} patient${this.patientsCount > 1 ? "s" : ""}  
    ${this.searchedValue.length > 0 ? (this.patientsCount > 1 ? "trouvés" : "trouvé") : ""}`
		if (this.searchedValue.length > 0) {
			this.noResultMessage = "Aucun patient trouvé"
		} else {
			this.noResultMessage = "Aucun patient"
		}
	}

}
